import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField
} from "@mui/material";
import axios from "axios";
import { useState } from "react";
import { SearchNotOrdered, Status } from "./types";
import { fCurrency } from "../utils/formatNumber";
import { CloseIcon } from "../../minimals/theme/overrides/CustomIcons";
import { processSearchStatuses } from "./processSearchStatuses";
import { useSearchesNotOrdered } from "./SearchesNotOrderedContext";
import ConfirmDialog from "../../minimals/components/confirm-dialog";

interface IProps {
  entity: SearchNotOrdered;
  setEntity: Function;
  setEntityTable?: Function;
  isOpen: boolean;
  orderNumberDisplay?: string;
  amount?: string;
  priority?: string;
  refetchData?: Function;
  statusOptions: Status[];
}

export default function EditStatusModal({
  entity,
  setEntity,
  orderNumberDisplay,
  amount,
  isOpen,
  refetchData,
  statusOptions
}: IProps) {
  const { setSearchesNotOrdered, searchesNotOrdered } = useSearchesNotOrdered();
  const [submitting, setSubmitting] = useState(false);
  const [showWaitingForClientWarning, setShowWaitingForClientWarning] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<Status | null>(
    statusOptions.find((s) => entity.status === s.fieldDisplayName || entity.status === s.value) ||
      statusOptions.find((s) => s.value === "None")!!
  );

  const saveStatus = async () => {
    await axios.post("/proxy/api/requestedtasks/update", {
      orderNumber: entity.orderNumber,
      task: {
        description: "County Search",
        status: selectedStatus?.value
      }
    });
    updateSelectedEntity();
  };

  const updateSelectedEntity = () => {
    if (!entity) return;
    const updatedEntity = {
      ...entity,
      status: selectedStatus?.fieldDisplayName
    } as SearchNotOrdered;
    const updatedData = processSearchStatuses(
      searchesNotOrdered.map((s) => (s.orderNumberWithPrefix === entity.orderNumberWithPrefix ? updatedEntity : s))
    );

    setSearchesNotOrdered(updatedData);

    setEntity(null);
  };

  const shouldWarnForWaitingForClient = () =>
    entity.status === "Ordered" && selectedStatus?.fieldDisplayName === "Waiting for Client";

  const save = async (warningConfirmed = false) => {
    if (!entity) return;
    if (!warningConfirmed && shouldWarnForWaitingForClient()) {
      setShowWaitingForClientWarning(true);
      return;
    }

    setShowWaitingForClientWarning(false);
    setSubmitting(true);
    try {
      await saveStatus();
    } finally {
      setSubmitting(false);
    }
    if (refetchData) {
      refetchData();
    }
  };

  const onClose = () => setEntity(null);

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>
          <Stack direction="row" spacing={2} alignItems="center" marginBottom={3}>
            <Box>
              Order Number {orderNumberDisplay || entity.orderNumber}
              {amount ? ` - ${fCurrency(amount)}` : ""}
            </Box>
          </Stack>
        </DialogTitle>
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent sx={{ paddingBottom: 3 }}>
          <Box paddingTop={1}>
            <Autocomplete
              size="small"
              options={statusOptions}
              defaultValue={selectedStatus}
              renderInput={(params) => <TextField {...params} label="Status" />}
              onChange={(e, value) => setSelectedStatus(value)}
              fullWidth
              getOptionLabel={(option) => option.fieldDisplayName}
            />
            <Stack direction="row" justifyContent="right" marginTop={2}>
              <Button variant="contained" size="small" onClick={() => save()} disabled={submitting}>
                {submitting ? "Saving..." : "Save"}
              </Button>
            </Stack>
          </Box>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={showWaitingForClientWarning}
        onClose={() => setShowWaitingForClientWarning(false)}
        title="Warning"
        content="the order will remove all record of the search request if you do so. If you did not yet cancel the search request with the searcher, do not proceed."
        action={
          <Button variant="contained" color="warning" onClick={() => save(true)} disabled={submitting}>
            Proceed
          </Button>
        }
      />
    </>
  );
}
